import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AiImportPayload, AiRuleLike, CompareTarget } from './ai-editor.types';
import { AiPaneComponent } from './ai-pane/ai-pane.component';
import { PssActionType, PssAmmoDesign, PssConditionType } from '../../../services/pss-auth.service';

// Re-export types so existing importers (crew-detail-modal, room-detail-modal) continue to work.
export type { AiImportRule, AiImportPayload, AiRuleLike, CompareTarget } from './ai-editor.types';

@Component({
  selector: 'app-ai-editor',
  standalone: true,
  imports: [CommonModule, FormsModule, AiPaneComponent],
  templateUrl: './ai-editor.component.html',
  styleUrls: ['./ai-editor.component.css']
})
export class AiEditorComponent implements OnInit, OnChanges {
  // ── Main pane inputs ──────────────────────────────────────────────────────
  @Input() rules: AiRuleLike[] = [];
  @Input() conditionTypes: PssConditionType[] = [];
  @Input() actionTypes: PssActionType[] = [];
  /** Room build pool in "Set Item {N}" order, see AiPaneComponent.itemsByIndex. */
  @Input() itemsByIndex: PssAmmoDesign[] = [];

  @Input() saving = false;
  @Input() error = '';
  @Input() emptyMessage = 'No AI rules set.';

  @Input() initialConditionId = '';
  @Input() initialActionId = '';

  @Input() targetType: 'crew' | 'room' = 'crew';
  @Input() targetId = '';
  @Input() startExpanded = false;
  @Input() targetName = '';
  @Input() targetSpriteId = '';
  @Input() userId = '';
  @Input() allianceId = '';
  @Input() userName = '';

  // ── Compare pane inputs ───────────────────────────────────────────────────
  @Input() compareTargets: CompareTarget[] = [];
  @Input() compareSaving = false;
  @Input() compareError = '';

  // ── Main pane outputs ─────────────────────────────────────────────────────
  @Output() addRule    = new EventEmitter<{ conditionTypeId: string; actionTypeId: string }>();
  @Output() deleteRule = new EventEmitter<any>();
  @Output() moveRule   = new EventEmitter<{ rule: any; newIndex: number }>();
  @Output() importRules  = new EventEmitter<AiImportPayload>();
  @Output() deleteAllRules = new EventEmitter<void>();

  // ── Compare pane outputs ──────────────────────────────────────────────────
  @Output() deleteAllCompareRules = new EventEmitter<string>();  @Output() editRule         = new EventEmitter<{ rule: any; conditionTypeId: string; actionTypeId: string }>();
  @Output() compareEditRule  = new EventEmitter<{ targetId: string; rule: any; conditionTypeId: string; actionTypeId: string }>();  @Output() compareAddRule     = new EventEmitter<{ targetId: string; conditionTypeId: string; actionTypeId: string }>();
  @Output() compareDeleteRule  = new EventEmitter<{ targetId: string; rule: any }>();
  @Output() compareMoveRule    = new EventEmitter<{ targetId: string; rule: any; newIndex: number }>();
  @Output() compareImportRules = new EventEmitter<{ targetId: string; payload: AiImportPayload }>();
  @Output() copyFromCompare    = new EventEmitter<string>();

  // ── Shell state ───────────────────────────────────────────────────────────
  expanded = false;
  showCompare = false;
  compareSearch = '';
  selectedCompare: CompareTarget | null = null;

  ngOnInit(): void {
    this.expanded = this.startExpanded;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['startExpanded']) {
      this.expanded = this.startExpanded;
    }
    if (changes['compareTargets'] && this.selectedCompare) {
      const updated = this.compareTargets.find(t => t.id === this.selectedCompare!.id);
      if (updated) this.selectedCompare = updated;
    }
  }

  // ── Compare panel: target selection ──────────────────────────────────────

  toggleCompare(): void {
    this.showCompare = !this.showCompare;
    if (!this.showCompare) {
      this.selectedCompare = null;
      this.compareSearch = '';
    }
  }

  filteredCompareTargets(): CompareTarget[] {
    const s = this.compareSearch.toLowerCase();
    return this.compareTargets.filter(t => t.name.toLowerCase().includes(s));
  }

  selectCompareTarget(t: CompareTarget): void {
    this.selectedCompare = t;
    this.compareSearch = '';
  }

  clearCompareSelection(): void {
    this.selectedCompare = null;
    this.compareSearch = '';
  }

  // ── Compare pane event forwarding ─────────────────────────────────────────

  onCmpRulesChanged(rules: AiRuleLike[]): void {
    if (this.selectedCompare) this.selectedCompare.rules = rules;
  }

  onCmpAddRule(event: { conditionTypeId: string; actionTypeId: string }): void {
    if (!this.selectedCompare) return;
    this.compareAddRule.emit({ targetId: this.selectedCompare.id, ...event });
  }

  onCmpDeleteRule(rule: AiRuleLike): void {
    if (!this.selectedCompare) return;
    this.compareDeleteRule.emit({ targetId: this.selectedCompare.id, rule });
  }

  onCmpMoveRule(event: { rule: AiRuleLike; newIndex: number }): void {
    if (!this.selectedCompare) return;
    this.compareMoveRule.emit({ targetId: this.selectedCompare.id, ...event });
  }

  onCmpImportRules(payload: AiImportPayload): void {
    if (!this.selectedCompare) return;
    this.compareImportRules.emit({ targetId: this.selectedCompare.id, payload });
  }

  onCmpDeleteAllRules(): void {
    if (!this.selectedCompare) return;
    this.deleteAllCompareRules.emit(this.selectedCompare.id);
  }

  onCmpEditRule(event: { rule: any; conditionTypeId: string; actionTypeId: string }): void {
    if (!this.selectedCompare) return;
    this.compareEditRule.emit({ targetId: this.selectedCompare.id, ...event });
  }
}
